import { Component, EventEmitter, Inject, Output, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { TaxonomyVersionDropdownComponent } from '../taxonomy-version-dropdown/taxonomy-version-dropdown.component';
import { RightPanelHeaderComponent } from '../right-panel-header/right-panel-header.component';
import { DropdownObjectType } from '../../shared/types';
import { TaxonomyApiService } from '../../services/api-services/taxonomy-api.service';

export interface CloneTaxonomyData {
  taxonomy_id: string;
  taxonomy_name: string;
  version_history_details: any[];
}

@Component({
  selector: 'clone-taxonomy-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, TaxonomyVersionDropdownComponent, RightPanelHeaderComponent],
  templateUrl: './clone-taxonomy-popup.component.html',
  styleUrls: ['./clone-taxonomy-popup.component.scss'],
})
export class CloneTaxonomyPopupComponent {
  @Output() cloneConfirmed = new EventEmitter<{
    taxonomy_id: string;
    version_id: string;
    taxonomy_name: string;
  }>();

  constructor(
    public dialogRef: MatDialogRef<CloneTaxonomyPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CloneTaxonomyData,
    private taxonomyApiService: TaxonomyApiService
  ) {}

  newTaxonomyName = signal<string>('');
  selectedVersion = signal<any>(null);
  selectedDropdownOption = signal<DropdownObjectType | null>(null);
  taxonomyDescription = signal<string>('');
  isNameValid = signal<boolean>(false);
  isVersionSelected = signal<boolean>(false);
  isLoading = signal<boolean>(true);
  existingTaxonomyNames = signal<string[]>([]);
  isNameUnique = signal<boolean>(true);

  versionOptions: DropdownObjectType[] = [];
  versionHistoryDetails: any[] = []; // Store the full version data

  ngOnInit() {
    this.fetchVersionHistory();
    this.loadExistingTaxonomyNames();
    // Keep taxonomy name empty by default
    this.newTaxonomyName.set('');
    this.isNameValid.set(false);
    this.validateTaxonomyName('');
  }

  private fetchVersionHistory() {
    this.isLoading.set(true);
    this.taxonomyApiService.getVersionHistoryById({ taxonomy_id: this.data.taxonomy_id }).subscribe({
      next: (response) => {
        if (response?.data?.version_history_details) {
          this.versionHistoryDetails = response.data.version_history_details;
          this.prepareVersionOptions(this.versionHistoryDetails);
        } else {
          // Fallback to passed data if API doesn't return expected format
          this.versionHistoryDetails = this.data.version_history_details || [];
          this.prepareVersionOptions(this.versionHistoryDetails);
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error fetching version history:', error);
        // Fallback to passed data on error
        this.versionHistoryDetails = this.data.version_history_details || [];
        this.prepareVersionOptions(this.versionHistoryDetails);
        this.isLoading.set(false);
      },
    });
  }

  private prepareVersionOptions(versionHistoryDetails: any[]) {
    if (versionHistoryDetails && Array.isArray(versionHistoryDetails)) {
      this.versionOptions = versionHistoryDetails.map((version: any) => {
        // Create label based on version and status
        let label = '';

        // Add version number if available
        if (version.version !== null && version.version !== undefined) {
          label = `Version ${version.version}`;
        } else {
          // Check if it's a draft with DraftVisibleToAll true
          if (version.status === 'draft' && version.DraftVisibleToAll === true) {
            label = 'Draft in Review';
          } else {
            label = 'Draft';
          }
        }

        // Determine status for the dropdown
        let status = '';
        if (version.status) {
          if (version.status.toLowerCase() === 'published') {
            status = 'published';
          } else if (version.status.toLowerCase() === 'draft') {
            if (version.DraftVisibleToAll === true) {
              status = 'review';
            } else {
              status = 'draft';
            }
          }
        } else if (version.ReadyForPublish) {
          status = 'review';
        }

        return {
          label: label,
          value: version.record_id,
          status: status,
        };
      });
    }
  }

  onNameChange(name: string) {
    this.newTaxonomyName.set(name);
    this.isNameValid.set(name.trim().length > 0);
    this.validateTaxonomyName(name);
  }

  onDescriptionChange(description: string) {
    this.taxonomyDescription.set(description);
  }

  loadExistingTaxonomyNames() {
    this.taxonomyApiService.getExistingTaxonomyNames({}).subscribe({
      next: (response) => {
        if (response?.success && response?.data) {
          this.existingTaxonomyNames.set(response.data);
        }
      },
      error: (err) => {
        console.error('Failed to fetch existing taxonomy names:', err);
      },
    });
  }

  validateTaxonomyName(name: string): void {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      this.isNameUnique.set(true);
      return;
    }

    const existingNames = this.existingTaxonomyNames();
    const isUnique = !existingNames.some((existingName) => existingName.toLowerCase() === trimmedName.toLowerCase());
    this.isNameUnique.set(isUnique);
  }

  onVersionSelect(version: DropdownObjectType) {
    // Store the selected dropdown option for display
    this.selectedDropdownOption.set(version);

    // Find the actual version object from the stored version history
    const actualVersion = this.findVersionByRecordId(version.value);
    this.selectedVersion.set(actualVersion);
    this.isVersionSelected.set(true);
  }

  private findVersionByRecordId(recordId: string): any {
    return this.versionHistoryDetails.find((v: any) => v.record_id === recordId);
  }

  onCancel(event: any): void {
    this.dialogRef.close({ event: 'close', data: { confirm: false } });
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
  }

  onOutsideClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  onConfirm(event: any) {
    if (this.isNameValid() && this.isVersionSelected() && this.selectedVersion() && this.isNameUnique()) {
      const cloneData = {
        taxonomy_id: this.data.taxonomy_id,
        version_id: this.selectedVersion().record_id, // Use record_id directly
        taxonomy_name: this.newTaxonomyName(),
        taxonomy_description: this.taxonomyDescription() || '',
      };

      this.dialogRef.close({
        event: 'clone',
        data: {
          confirm: true,
          cloneData: cloneData,
        },
      });
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
  }

  isFormValid(): boolean {
    return this.isNameValid() && this.isVersionSelected() && this.isNameUnique();
  }
}
